import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";

import API from "../../API";
import { formatDate } from "./MarketplaceBase";

const TARGETS = [
  { value: "customer", label: "Müşteri" },
  { value: "quoting_drivers", label: "Teklif veren nakliyeciler" },
  { value: "contacted_drivers", label: "Müşterinin aradığı nakliyeciler" },
];
const TARGET_LABELS = { customer: "Müşteri", driver: "Nakliyeci", quoting_drivers: "Teklif veren nakliyeciler", contacted_drivers: "Aranan nakliyeciler" };
const SOURCE_LABELS = { manual: "Manuel", system: "Otomatik" };

const targetText = (entry) => {
  const base = TARGET_LABELS[entry.target] || entry.target || "-";
  return entry.target === "customer" ? base : `${base} (${entry.recipientCount})`;
};

/*
 * "Send Notification" for one marketplace request plus its history — the
 * pushes operations sent by hand and the ones the system sent automatically.
 */
export function useRequestNotifications(serviceType, requestId) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    if (!requestId) return;
    API.fetchClient("panel/marketplacenotifications", { serviceType, requestId, action: "history" }).then((result) => {
      setItems(result?.notifications || []);
    });
  }, [serviceType, requestId]);
  useEffect(() => { load(); }, [load]);
  return { items, reload: load };
}

export function SendNotificationDialog({ open, serviceType, requestId, onClose, onSent }) {
  const [target, setTarget] = useState("customer");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = () => {
    setBusy(true);
    setError(null);
    API.fetchClient("panel/marketplacenotifications", { serviceType, requestId, action: "send", target, title: title.trim(), body: body.trim() }).then((result) => {
      setBusy(false);
      if (result?.ok) { setTitle(""); setBody(""); onSent?.(); onClose(); return; }
      setError(result?.message || "Bildirim gönderilemedi.");
    });
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>Bildirim Gönder</DialogTitle>
      <DialogContent>
        <TextField select fullWidth size="small" label="Alıcı" value={target} onChange={(event) => setTarget(event.target.value)} sx={{ marginTop: 1 }}>
          {TARGETS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
        </TextField>
        <TextField fullWidth size="small" label="Başlık" value={title} onChange={(event) => setTitle(event.target.value)} sx={{ marginTop: 2 }} inputProps={{ maxLength: 120 }} />
        <TextField fullWidth size="small" multiline minRows={3} label="Mesaj" value={body} onChange={(event) => setBody(event.target.value)} sx={{ marginTop: 2 }} inputProps={{ maxLength: 500 }} />
        {error ? <Typography style={{ color: "#B91C1C", fontSize: 13, marginTop: 10 }}>{error}</Typography> : null}
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 18px" }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 700, color: "#64748B" }}>Vazgeç</Button>
        <Button
          variant="contained" disableElevation disabled={busy || !title.trim() || !body.trim()} onClick={send}
          sx={{ textTransform: "none", fontWeight: 800, backgroundColor: "#4F46E5", "&:hover": { backgroundColor: "#4338CA" }, borderRadius: "10px" }}
        >
          {busy ? "Gönderiliyor…" : "Gönder"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function NotificationHistory({ items }) {
  if (items === null) return <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Yükleniyor…</Typography>;
  if (!items.length) return <Typography style={{ fontSize: 13, color: "#64748B", padding: "8px 0" }}>Bu talep için henüz bildirim gönderilmedi.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Tarih</TableCell>
          <TableCell>Bildirim</TableCell>
          <TableCell>Alıcı</TableCell>
          <TableCell>Kaynak</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((entry) => (
          <TableRow key={entry.id}>
            <TableCell style={{ whiteSpace: "nowrap" }}>{formatDate(entry.sentAt)}</TableCell>
            <TableCell>
              <Typography style={{ fontSize: 13, fontWeight: 700 }}>{entry.title || "-"}</Typography>
              <Typography style={{ fontSize: 12.5, color: "#475569" }}>{entry.body || "-"}</Typography>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{targetText(entry)}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{SOURCE_LABELS[entry.source] || entry.source}{entry.sentBy ? ` · ${entry.sentBy}` : ""}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export const NOTIFICATIONS_ICON = <NotificationsActiveOutlinedIcon sx={{ fontSize: 19 }} />;
